import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useMessages } from "../../context/MessageContext";
import { useTheme } from "../../context/ThemeContext";
import {
  Building2,
  Package,
  TicketIcon,
  BarChart3,
  Menu,
  X,
  LogOut,
  User,
  ChevronRight,
  Users,
  Search,
  Bell,
  PieChart,
  CircleDot,
  Mail,
  SlidersHorizontal,
  ShieldAlert,
  FileText,
  FolderOpen,
  Calendar,
  CalendarRange,
  Settings as SettingsIcon,
  Shield,
  ShoppingBag,
  ShoppingCart,
  MessageSquare,
  Briefcase,
  Sun,
  Moon,
  Sparkles,
  GraduationCap,
  BookOpen,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import useTickets from "../../api/ticket";
import useCompany from "../../api/company";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const DashboardLayout = () => {
  const { company, logout, isAdmin } = useAuth();
  const { unreadMessagesCount } = useMessages();
  const { theme, toggleTheme } = useTheme();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeLink, setActiveLink] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef(null);
  const touchStartX = useRef(null);

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    if (sidebarOpen && window.innerWidth < 1024) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [sidebarOpen]);

  // For tickets counts
  const { getAllTickets } = useTickets();
  const { getLogoUrl } = useCompany();
  const [openTicketsCount, setOpenTicketsCount] = useState(0);
  const [companyLogoUrl, setCompanyLogoUrl] = useState(null);

  // Fetch counts on load
  useEffect(() => {
    if (isAdmin) {
      fetchOpenTicketsCount();
    }
  }, [isAdmin]);

  const fetchOpenTicketsCount = async () => {
    try {
      const tickets = await getAllTickets();
      const openCount =
        tickets?.filter((ticket) => ticket.status === "open")?.length || 0;
      setOpenTicketsCount(openCount);
    } catch (error) {
      console.error("Error fetching tickets count:", error);
    }
  };

  // Fetch company logo
  useEffect(() => {
    if (company?.id) {
      getLogoUrl(company.id).then((data) => {
        if (data?.logoUrl) setCompanyLogoUrl(data.logoUrl);
      });
    }
  }, [company?.id, getLogoUrl]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const hasPermission = (featureId) => {
    if (!company?.permissions) return true;
    return (
      company.permissions[featureId] === true ||
      company.permissions[featureId] === 1
    );
  };

  // Navigation grouped by category
  const navGroups = [
    {
      label: null, // no header for top-level items
      items: [
        { name: "Overzicht", href: "/dashboard", icon: Building2, id: "overview" },
      ],
    },
    {
      label: "Communicatie",
      items: [
        {
          name: "Inbox",
          href: "/dashboard/inbox",
          icon: Mail,
          notificationCount: unreadMessagesCount,
          notificationColor: "bg-blue-500",
          id: "inbox",
        },
        { name: "Tickets", href: "/dashboard/tickets", icon: TicketIcon, id: "tickets" },
      ],
    },
    {
      label: "Website",
      items: [
        { name: "Items", href: "/dashboard/items", icon: Package, id: "items" },
        { name: "Inschrijvingen", href: "/dashboard/events", icon: CalendarRange, id: "events" },
        { name: "Reservaties", href: "/dashboard/reservations", icon: Calendar, id: "reservations" },
        { name: "Pop-ups", href: "/dashboard/popups", icon: MessageSquare, id: "popups" },
      ],
    },
    {
      label: "Verkoop",
      items: [
        { name: "Winkel", href: "/dashboard/shop", icon: ShoppingBag, id: "shop" },
        { name: "Bestellingen", href: "/dashboard/orders", icon: ShoppingCart, id: "orders" },
        { name: "Configurator", href: "/dashboard/configurator", icon: SlidersHorizontal, id: "configurator" },
      ],
    },
    {
      label: "Leren & Werving",
      items: [
        { name: "Leerplatform", href: "/dashboard/learning-platform", icon: GraduationCap, id: "learningPlatform" },
        { name: "Sollicitaties", href: "/dashboard/jobs", icon: Briefcase, id: "jobs" },
      ],
    },
    {
      label: "Analyse",
      items: [
        { name: "Statistieken", href: "/dashboard/statistics", icon: BarChart3, id: "statistics" },
        { name: "AI Video Analist", href: "/dashboard/ai-video-analyst", icon: Sparkles, id: "aiVideoAnalyst" },
        { name: "Bestanden", href: "/dashboard/files", icon: FolderOpen, id: "files" },
      ],
    },
  ];

  // Filter each group's items by permission, then drop empty groups
  const visibleNavGroups = navGroups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => !item.id || item.id === "overview" || hasPermission(item.id),
      ),
    }))
    .filter((group) => group.items.length > 0);

  // Flat list for active-link detection and mobile rendering
  const userNavigation = visibleNavGroups.flatMap((g) => g.items);

  // Admin-specific navigation
  const adminNavigation = [
    {
      name: "Admin Tickets",
      href: "/dashboard/admin-tickets",
      icon: TicketIcon,
      notificationCount: openTicketsCount,
      notificationColor: "bg-blue-500",
    },
    { name: "Admin Items", href: "/dashboard/admin-items", icon: Package },
    {
      name: "Admin Bestanden",
      href: "/dashboard/admin-files",
      icon: FolderOpen,
    },
    {
      name: "Admin Pop-ups",
      href: "/dashboard/admin-popups",
      icon: MessageSquare,
    },
    {
      name: "Admin Configurators",
      href: "/dashboard/admin-configurators",
      icon: SlidersHorizontal,
    },
    {
      name: "Admin Sollicitaties",
      href: "/dashboard/admin-jobs",
      icon: Briefcase,
    },
    {
      name: "Admin Winkel",
      href: "/dashboard/admin-shop",
      icon: ShoppingBag,
    },
    { name: "Bedrijven", href: "/dashboard/companies", icon: Users },
    { name: "Documentatie", href: "/docs", icon: BookOpen },
  ];

  // Combine navigation based on user role
  const navigation = isAdmin
    ? [...userNavigation, ...adminNavigation]
    : userNavigation;

  // Set active link based on location
  useEffect(() => {
    const currentPath = location.pathname;
    const current = navigation.find(
      (item) =>
        currentPath === item.href ||
        (currentPath.startsWith(item.href) && item.href !== "/dashboard"),
    );
    setActiveLink(current ? current.name : navigation[0].name);
  }, [location, navigation]);

  // Mobile sidebar toggle
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const isDark = theme === "dark";

  // Render a single nav item — flat Revolut-style
  const renderNavItem = (item) => (
    <NavLink
      key={item.name}
      to={item.href}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150
        ${isActive
          ? isDark
            ? "bg-white/[0.06] text-white"
            : "bg-black/[0.04] text-gray-900"
          : isDark
            ? "text-gray-500 hover:text-gray-200 hover:bg-white/[0.03]"
            : "text-gray-500 hover:text-gray-800 hover:bg-black/[0.02]"
        }`
      }
      end={item.href === "/dashboard"}
      onClick={() => {
        setActiveLink(item.name);
        if (window.innerWidth < 1024) setSidebarOpen(false);
      }}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <motion.div
              layoutId="activeNav"
              className="absolute left-[-13px] top-1/2 -translate-y-1/2 w-[3px] h-4 bg-blue-500 rounded-full"
              transition={{ type: "spring", stiffness: 400, damping: 28 }}
            />
          )}

          <item.icon
            className={`h-[16px] w-[16px] flex-shrink-0 transition-colors duration-150 ${isActive
              ? isDark
                ? "text-white"
                : "text-gray-900"
              : isDark
                ? "text-gray-600"
                : "text-gray-400"
              }`}
            strokeWidth={isActive ? 2 : 1.6}
          />

          <span className="truncate">{item.name}</span>

          {item.notificationCount > 0 && (
            <span
              className={`ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-md min-w-[20px] text-center leading-none ${isDark
                ? "bg-white/10 text-white"
                : "bg-black/[0.06] text-gray-700"
                }`}
            >
              {item.notificationCount}
            </span>
          )}
        </>
      )}
    </NavLink>
  );

  return (
    <div
      className={`h-screen flex overflow-hidden transition-colors duration-200 ${isDark ? "bg-[#0a0a0a]" : "bg-[#f8f8fa]"}`}
    >
      {/* Mobile overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 lg:hidden bg-black/50 backdrop-blur-sm"
            onClick={toggleSidebar}
          />
        )}
      </AnimatePresence>

      {/* Sidebar — desktop (static) */}
      <aside
        className={`
          hidden lg:flex
          w-[236px] flex-col border-r
          ${isDark ? "bg-[#0a0a0a] border-white/[0.06]" : "bg-white border-gray-200/70"}
        `}
      >
        {/* Logo */}
        <div className="h-14 flex items-center px-5">
          <span
            className={`text-[15px] font-zichtbaar font-black tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
          >
            ZICHTBAAR{" "}
            <span className={`${isDark ? "text-[#4d7ee0]" : "text-[#1E4FCF]"}`}>
              DASH
            </span>
          </span>
        </div>

        {/* Nav */}
        <div className="flex-1 px-3 pt-2 pb-4 overflow-y-auto no-scrollbar">
          {visibleNavGroups.map((group, gi) => (
            <div key={group.label ?? "__top"} className={gi > 0 ? "mt-4" : ""}>
              {group.label && (
                <p className={`px-3 mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] ${isDark ? "text-gray-700" : "text-gray-400"}`}>
                  {group.label}
                </p>
              )}
              <nav className="space-y-0.5">
                {group.items.map(renderNavItem)}
              </nav>
            </div>
          ))}

          {isAdmin && (
            <div className="mt-4">
              <div className={`flex items-center gap-1.5 px-3 mb-1`}>
                <ShieldAlert className={`h-3 w-3 ${isDark ? "text-gray-600" : "text-gray-400"}`} />
                <span className={`text-[10px] uppercase tracking-[0.12em] font-semibold ${isDark ? "text-gray-600" : "text-gray-400"}`}>
                  Admin
                </span>
              </div>
              <nav className="space-y-0.5">
                {adminNavigation.map(renderNavItem)}
              </nav>
            </div>
          )}
        </div>

        {/* User section */}
        <div
          className={`px-3 py-3 border-t ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
        >
          {company && (
            <div className="flex items-center gap-2.5 px-2">
              {companyLogoUrl ? (
                <div
                  className={`flex-shrink-0 h-8 w-8 rounded-lg overflow-hidden p-1 ${isDark ? "bg-white/[0.06] border border-white/[0.08]" : "bg-gray-50 border border-gray-200"}`}
                >
                  <img
                    src={companyLogoUrl}
                    alt={company.name}
                    className="h-full w-full object-contain"
                  />
                </div>
              ) : (
                <div
                  className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-[12px] font-semibold ${isDark ? "bg-white/[0.08] text-gray-300" : "bg-gray-100 text-gray-600"}`}
                >
                  {company.name?.charAt(0)?.toUpperCase() || "Z"}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p
                  className={`text-[12px] font-medium truncate ${isDark ? "text-gray-200" : "text-gray-800"}`}
                >
                  {company.name}
                </p>
                <p
                  className={`text-[10px] truncate ${isDark ? "text-gray-600" : "text-gray-400"}`}
                >
                  {company.email}
                </p>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={`p-1 rounded-md transition-colors ${isDark ? "text-gray-600 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 15 15"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3.625 7.5C3.625 8.12132 3.12132 8.625 2.5 8.625C1.87868 8.625 1.375 8.12132 1.375 7.5C1.375 6.87868 1.87868 6.375 2.5 6.375C3.12132 6.375 3.625 6.87868 3.625 7.5ZM8.625 7.5C8.625 8.12132 8.12132 8.625 7.5 8.625C6.87868 8.625 6.375 8.12132 6.375 7.5C6.375 6.87868 6.87868 6.375 7.5 6.375C8.12132 6.375 8.625 6.87868 8.625 7.5ZM13.625 7.5C13.625 8.12132 13.1213 8.625 12.5 8.625C11.8787 8.625 11.375 8.12132 11.375 7.5C11.375 6.87868 11.8787 6.375 12.5 6.375C13.1213 6.375 13.625 6.87868 13.625 7.5Z"
                        fill="currentColor"
                        fillRule="evenodd"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className={`rounded-lg shadow-lg border min-w-[160px] ${isDark ? "bg-[#141414] border-white/[0.08]" : "bg-white border-gray-200"}`}
                >
                  <DropdownMenuLabel
                    className={`text-[11px] font-medium ${isDark ? "text-gray-500" : "text-gray-400"}`}
                  >
                    {company.email}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator
                    className={isDark ? "bg-white/[0.06]" : "bg-gray-100"}
                  />
                  <DropdownMenuItem
                    onClick={() => navigate("/dashboard/privacy")}
                    className={`cursor-pointer rounded-md text-[12px] ${isDark ? "text-gray-300 hover:bg-white/[0.05] focus:bg-white/[0.05]" : "text-gray-600 hover:bg-gray-50 focus:bg-gray-50"}`}
                  >
                    <Shield className="mr-2 h-3.5 w-3.5" />
                    Privacybeleid
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className={`cursor-pointer rounded-md text-[12px] ${isDark ? "text-gray-300 hover:bg-white/[0.05] focus:bg-white/[0.05]" : "text-gray-600 hover:bg-gray-50 focus:bg-gray-50"}`}
                  >
                    <LogOut className="mr-2 h-3.5 w-3.5 text-red-400" />
                    Uitloggen
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </aside>

      {/* Sidebar — mobile (animated drawer) */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.aside
            ref={sidebarRef}
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 300 }}
            onTouchStart={(e) => {
              touchStartX.current = e.touches[0].clientX;
            }}
            onTouchMove={(e) => {
              if (touchStartX.current === null) return;
              const diff = touchStartX.current - e.touches[0].clientX;
              if (diff > 80) {
                setSidebarOpen(false);
                touchStartX.current = null;
              }
            }}
            onTouchEnd={() => {
              touchStartX.current = null;
            }}
            className={`
              fixed inset-y-0 left-0 z-50 w-[280px] max-w-[85vw]
              flex flex-col border-r lg:hidden
              ${isDark ? "bg-[#0a0a0a] border-white/[0.06]" : "bg-white border-gray-200/70"}
            `}
          >
            {/* Logo + close */}
            <div className="h-14 flex items-center justify-between px-5">
              <span
                className={`text-[15px] font-zichtbaar font-black tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
              >
                ZICHTBAAR{" "}
                <span
                  className={`${isDark ? "text-[#4d7ee0]" : "text-[#1E4FCF]"}`}
                >
                  DASH
                </span>
              </span>
              <button
                className={`p-2 -mr-1 rounded-lg transition-colors ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
                onClick={toggleSidebar}
              >
                <X size={18} />
              </button>
            </div>

            {/* Nav */}
            <div className="flex-1 px-3 pt-2 pb-4 overflow-y-auto no-scrollbar">
              {visibleNavGroups.map((group, gi) => (
                <div key={group.label ?? "__top"} className={gi > 0 ? "mt-4" : ""}>
                  {group.label && (
                    <p className={`px-3 mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] ${isDark ? "text-gray-700" : "text-gray-400"}`}>
                      {group.label}
                    </p>
                  )}
                  <nav className="space-y-0.5">
                    {group.items.map(renderNavItem)}
                  </nav>
                </div>
              ))}

              {isAdmin && (
                <div className="mt-4">
                  <div className={`flex items-center gap-1.5 px-3 mb-1`}>
                    <ShieldAlert className={`h-3 w-3 ${isDark ? "text-gray-600" : "text-gray-400"}`} />
                    <span className={`text-[10px] uppercase tracking-[0.12em] font-semibold ${isDark ? "text-gray-600" : "text-gray-400"}`}>
                      Admin
                    </span>
                  </div>
                  <nav className="space-y-0.5">
                    {adminNavigation.map(renderNavItem)}
                  </nav>
                </div>
              )}
            </div>

            {/* User section */}
            <div
              className={`px-3 py-3 border-t ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
            >
              {company && (
                <div className="flex items-center gap-2.5 px-2">
                  {companyLogoUrl ? (
                    <div
                      className={`flex-shrink-0 h-8 w-8 rounded-lg overflow-hidden p-1 ${isDark ? "bg-white/[0.06] border border-white/[0.08]" : "bg-gray-50 border border-gray-200"}`}
                    >
                      <img
                        src={companyLogoUrl}
                        alt={company.name}
                        className="h-full w-full object-contain"
                      />
                    </div>
                  ) : (
                    <div
                      className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-[12px] font-semibold ${isDark ? "bg-white/[0.08] text-gray-300" : "bg-gray-100 text-gray-600"}`}
                    >
                      {company.name?.charAt(0)?.toUpperCase() || "Z"}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-[12px] font-medium truncate ${isDark ? "text-gray-200" : "text-gray-800"}`}
                    >
                      {company.name}
                    </p>
                    <p
                      className={`text-[10px] truncate ${isDark ? "text-gray-600" : "text-gray-400"}`}
                    >
                      {company.email}
                    </p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className={`p-2 rounded-lg transition-colors ${isDark ? "text-gray-500 hover:text-red-400 hover:bg-white/[0.04]" : "text-gray-400 hover:text-red-500 hover:bg-gray-50"}`}
                  >
                    <LogOut size={15} />
                  </button>
                </div>
              )}
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header — minimal */}
        <header
          className={`w-full h-12 flex items-center px-4 lg:px-6 z-10 transition-colors duration-200 border-b
            ${isDark
              ? "bg-[#0a0a0a] border-white/[0.06]"
              : "bg-white border-gray-200/70"
            }`}
        >
          <button
            className={`lg:hidden p-1.5 -ml-1 rounded-md transition-colors ${isDark ? "text-gray-500 hover:text-white" : "text-gray-400 hover:text-gray-700"}`}
            onClick={toggleSidebar}
          >
            <Menu size={18} />
          </button>

          <div className="ml-3 lg:ml-0">
            {activeLink && (
              <motion.span
                className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                key={activeLink}
              >
                {activeLink}
              </motion.span>
            )}
          </div>

          <div className="ml-auto flex items-center gap-1">
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-colors ${isDark
                ? "text-gray-500 hover:text-gray-300 hover:bg-white/[0.04]"
                : "text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                }`}
            >
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
            </button>

            <button
              onClick={() => navigate("/dashboard/inbox")}
              className={`relative p-2 rounded-lg transition-colors ${isDark
                ? "text-gray-500 hover:text-gray-300 hover:bg-white/[0.04]"
                : "text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                }`}
            >
              <Bell size={16} />
              {unreadMessagesCount > 0 && (
                <span className="absolute top-1 right-1 h-2 w-2 bg-blue-500 rounded-full" />
              )}
            </button>
          </div>
        </header>

        {/* Content */}
        <main
          className={`flex-1 overflow-y-auto transition-colors duration-200 relative ${isDark ? "bg-[#0a0a0a]" : "bg-[#f8f8fa]"}`}
        >
          {/* Animated glow orbs — dark mode only */}
          {isDark && (
            <>
              <motion.div
                className="fixed w-[600px] h-[600px] rounded-full pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle, rgba(30,79,207,0.18) 0%, transparent 70%)",
                  top: "0%",
                  left: "10%",
                  filter: "blur(100px)",
                }}
                animate={{
                  x: [0, 100, -40, 0],
                  y: [0, -60, 70, 0],
                  scale: [1, 1.15, 0.9, 1],
                }}
                transition={{
                  duration: 14,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
              <motion.div
                className="fixed w-[450px] h-[450px] rounded-full pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle, rgba(59,130,246,0.14) 0%, transparent 70%)",
                  bottom: "5%",
                  right: "5%",
                  filter: "blur(90px)",
                }}
                animate={{
                  x: [0, -80, 50, 0],
                  y: [0, 50, -80, 0],
                  scale: [1, 0.85, 1.2, 1],
                }}
                transition={{
                  duration: 18,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
              <motion.div
                className="fixed w-[300px] h-[300px] rounded-full pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)",
                  top: "40%",
                  right: "30%",
                  filter: "blur(70px)",
                }}
                animate={{
                  x: [0, 50, -60, 0],
                  y: [0, -40, 30, 0],
                  scale: [1, 1.3, 0.8, 1],
                }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            </>
          )}
          {/* De zaalplan-builder wil elke pixel: geen maximumbreedte en geen
              padding, zodat de gereedschapspanelen tegen de schermranden staan
              en het canvas zo groot mogelijk is. */}
          <div
            className={
              location.pathname.includes("/builder/")
                ? "relative z-10"
                : "relative z-10 max-w-7xl mx-auto px-4 lg:px-6 py-5 lg:py-6"
            }
          >
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
